// src/admin.js
import { useEffect, useState, useMemo } from "react";
import "./admin.css";
import { API_BASE, ADMIN_KEY, ICS_FEED_URL } from "./apiConfig";


import {
  auth,
  db,
  setMessageStatus,
  removeMessage,
} from "./firebase";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";




export default function Admin() {
  // ──────────────────────────────
  // ÉTATS
  // ──────────────────────────────
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });

  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  // barrière “mot de passe”
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessInput, setAccessInput] = useState("");
  const [wrong, setWrong] = useState(false);

  // messages de statut (API)
  const [syncStatus, setSyncStatus] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  const [acceptStatus, setAcceptStatus] = useState(null);
  const [cancelStatus, setCancelStatus] = useState(null);

  const ACCESS_PASSWORD = "?leSorbier/CCL/245!";

  // ──────────────────────────────
  // INIT
  // ──────────────────────────────

  // si on a déjà passé le mot de passe
  useEffect(() => {
    if (localStorage.getItem("adminAccess") === "true") {
      setAccessGranted(true);
    }
  }, []);

  // suivre la connexion firebase
  useEffect(() => {
    return onAuthStateChanged(auth, setUser);
  }, []);

  // ──────────────────────────────
  // FONCTIONS UTILES
  // ──────────────────────────────

  // 1) valider la barrière locale
  const handleAccess = (e) => {
    e.preventDefault();
    if (accessInput === ACCESS_PASSWORD) {
      localStorage.setItem("adminAccess", "true");
      setAccessGranted(true);
    } else {
      setWrong(true);
      setTimeout(() => setWrong(false), 2000);
    }
  };

  // 2) login firebase
  const login = async (e) => {
    e.preventDefault();
    await signInWithEmailAndPassword(
      auth,
      loginForm.email,
      loginForm.password
    );
  };

  // 3) logout complet
  const logout = async () => {
    await signOut(auth);
  };

  // 4) mettre à jour un message dans le state local
  const updateMessageLocalStatus = (id, newStatus) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, status: newStatus } : m))
    );
    // utiliser la version fonctionnelle
    setSelectedMessage((prev) =>
      prev && prev.id === id ? { ...prev, status: newStatus } : prev
    );
  };

  // 5) recharger les messages Firestore
  const loadMessages = async () => {
    if (!user) return;
    setLoading(true);
    try {
      let qRef = query(
        collection(db, "messages"),
        orderBy("createdAt", "desc")
      );

      if (filter !== "all") {
        qRef = query(
          collection(db, "messages"),
          where("status", "==", filter),
          orderBy("createdAt", "desc")
        );
      }

      const snap = await getDocs(qRef);
      const raw = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setMessages(raw);
    } finally {
      setLoading(false);
    }
  };

  // recharger quand user / filtre change
  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line
  }, [user, filter]);

  // ──────────────────────────────
  // TRI LOCAL
  // ──────────────────────────────
  const sortedMessages = useMemo(() => {
    const copy = [...messages];
    copy.sort((a, b) => {
      let va, vb;

      if (sortBy === "createdAt") {
        const da = a.createdAt?.toDate?.() || new Date(0);
        const dbb = b.createdAt?.toDate?.() || new Date(0);
        va = da.getTime();
        vb = dbb.getTime();
      } else if (sortBy === "nom") {
        va = (a.prenom + " " + a.nom).toLowerCase();
        vb = (b.prenom + " " + b.nom).toLowerCase();
      } else if (sortBy === "status") {
        va = a.status || "";
        vb = b.status || "";
      } else {
        va = a[sortBy] || "";
        vb = b[sortBy] || "";
      }

      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [messages, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (col === sortBy) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  // ──────────────────────────────
  // APPELS API BACK
  // ──────────────────────────────

  // A. synchroniser les .ics → maison.txt (Firebase Functions)
  const handleSyncCalendrier = async () => {
    setSyncStatus(null);
    try {
      const res = await fetch(`${API_BASE}/syncCalendrier`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-key": ADMIN_KEY,
        },
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        setSyncStatus({
          type: "err",
          msg:
            (data && data.error) ||
            "Erreur serveur lors de la synchronisation",
        });
        return;
      }

      setSyncStatus({
        type: "ok",
        msg: data.message || "Calendrier mis à jour",
      });
    } catch (err) {
      setSyncStatus({ type: "err", msg: err.message });
    }
  };


  // B. lien ICS pour les sites externes
  const handleExportICS = () => {
    // on affiche le lien dans le message de statut
    setExportStatus({
      type: "ok",
      msg: `Lien ICS à copier dans Airbnb/Booking/Abritel : ${ICS_FEED_URL}`,
    });

    // et on ouvre le fichier ICS dans un nouvel onglet pour vérification
    window.open(ICS_FEED_URL, "_blank");
  };




  // C. accepter une réservation (version Firebase Functions)
  const handleAcceptReservation = async (messageObj) => {
  setAcceptStatus(null);
  setCancelStatus(null);
  try {
    const res = await fetch(`${API_BASE}/acceptReservation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-admin-key": ADMIN_KEY,
      },
      body: JSON.stringify({ periodes: messageObj.periodes || "" }),
    });

    const data = await res.json();

    if (!res.ok || !data.ok) {
      setAcceptStatus({
        type: "err",
        msg:
          (data && data.error) ||
          "Erreur serveur lors de l'acceptation",
      });
      return;
    }

    updateMessageLocalStatus(messageObj.id, "accepted");
    await setMessageStatus(messageObj.id, "accepted");

    setAcceptStatus({
      type: "ok",
      msg:
        data.message ||
        "Réservation acceptée, dates bloquées dans le calendrier",
    });
  } catch (err) {
    setAcceptStatus({ type: "err", msg: err.message });
  }
};

  // D. annuler une réservation acceptée (Firebase Functions)
  const handleCancelReservation = async (messageObj) => {
    setCancelStatus(null);
    setAcceptStatus(null);
    try {
      const res = await fetch(`${API_BASE}/cancelReservation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-key": ADMIN_KEY,
        },
        body: JSON.stringify({ periodes: messageObj.periodes || "" }),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        setCancelStatus({
          type: "err",
          msg:
            (data && data.error) ||
            "Erreur serveur lors de l'annulation",
        });
        return;
      }

      updateMessageLocalStatus(messageObj.id, "read");
      await setMessageStatus(messageObj.id, "read");

      setCancelStatus({
        type: "ok",
        msg:
          data.message ||
          "Réservation annulée, dates libérées dans le calendrier",
      });
    } catch (err) {
      setCancelStatus({ type: "err", msg: err.message });
    }
  };



  // E. changer de statut (Lu / Archiver) mais ne pas casser accepted
  const handleSetStatus = async (id, status) => {
    const msg = messages.find((m) => m.id === id);
    if (msg && msg.status === "accepted" && status !== "accepted") {
      alert("Cette demande est acceptée. Annule-la d'abord pour changer le statut.");
      return;
    }
    await setMessageStatus(id, status);
    updateMessageLocalStatus(id, status);
  };

  // F. supprimer une demande (si accepted → annule avant)
  const handleDelete = async (id) => {
    const msg = messages.find((m) => m.id === id);
    if (!msg) return;

    if (!window.confirm("Supprimer ce message ?")) return;


    // s'il est accepté on annule d'abord au back (Firebase Functions)
    if (msg.status === "accepted") {
      try {
        const res = await fetch(`${API_BASE}/cancelReservation`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-admin-key": ADMIN_KEY,
          },
          body: JSON.stringify({ periodes: msg.periodes || "" }),
        });

        const data = await res.json();

        if (!res.ok || !data.ok) {
          alert(
            "Impossible d'annuler les dates dans le calendrier : " +
              (data && data.error
                ? data.error
                : "Erreur serveur")
          );
          return;
        }
      } catch (err) {
        alert("Erreur d'annulation dans le calendrier : " + err.message);
        return;
      }
    }


    await removeMessage(id);
    setMessages((prev) => prev.filter((m) => m.id !== id));
    if (selectedMessage && selectedMessage.id === id) {
      setSelectedMessage(null);
    }
  };

  // ──────────────────────────────
  // 8. AFFICHAGE
  // ──────────────────────────────

  // 1ère barrière
  if (!accessGranted) {
    return (
      <div className="admin-login-wrapper">
        <div className="admin-card">
          <h2 className="admin-card-title">Accès restreint</h2>
          <p className="admin-card-sub">Veuillez entrer le mot de passe d’accès :</p>

          <form className="admin-login-form" onSubmit={handleAccess}>
            <input
              className="admin-input"
              type="password"
              placeholder="Mot de passe"
              value={accessInput}
              onChange={(e) => setAccessInput(e.target.value)}
              required
            />
            <button className="btn-primary" type="submit">
              Entrer
            </button>
          </form>

          {wrong && (
            <p style={{ color: "red", marginTop: "10px" }}>
              ❌ Mot de passe incorrect
            </p>
          )}
        </div>
      </div>
    );
  }

  // 2ème barrière
  if (!user) {
    return (
      <div className="admin-login-wrapper">
        <div className="admin-card">
          <h2 className="admin-card-title">Espace Admin</h2>
          <p className="admin-card-sub">Connexion requise</p>

          <form className="admin-login-form" onSubmit={login}>
            <label className="admin-label">
              Email
              <input
                className="admin-input"
                type="email"
                value={loginForm.email}
                onChange={(e) =>
                  setLoginForm({ ...loginForm, email: e.target.value })
                }
                required
              />
            </label>
            <label className="admin-label">
              Mot de passe
              <input
                className="admin-input"
                type="password"
                value={loginForm.password}
                onChange={(e) =>
                  setLoginForm({ ...loginForm, password: e.target.value })
                }
                required
              />
            </label>
            <button className="btn-primary" type="submit">
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }

  // dashboard
  return (
    <div className="admin-layout">
      {/* HEADER */}
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-title">Tableau de bord réservation</div>
          <div className="admin-subtitle">
            {loading ? "Chargement..." : `${sortedMessages.length} message(s)`}
          </div>

          {syncStatus && (
            <div style={{ marginTop: "6px", color: syncStatus.type === "ok" ? "green" : "red" }}>
              {syncStatus.type === "ok" ? "✅" : "❌"} {syncStatus.msg}
            </div>
          )}
          {exportStatus && (
            <div style={{ marginTop: "6px", color: exportStatus.type === "ok" ? "green" : "red" }}>
              {exportStatus.type === "ok" ? "✅" : "❌"} {exportStatus.msg}
            </div>
          )}
          {acceptStatus && (
            <div style={{ marginTop: "6px", color: acceptStatus.type === "ok" ? "green" : "red" }}>
              {acceptStatus.type === "ok" ? "✅" : "❌"} {acceptStatus.msg}
            </div>
          )}
          {cancelStatus && (
            <div style={{ marginTop: "6px", color: cancelStatus.type === "ok" ? "green" : "red" }}>
              {cancelStatus.type === "ok" ? "✅" : "❌"} {cancelStatus.msg}
            </div>
          )}
        </div>

        <div className="admin-header-right">
          <select
            className="admin-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">Tous</option>
            <option value="new">Nouveaux</option>
            <option value="read">Lus</option>
            <option value="accepted">Acceptés</option>
            <option value="archived">Archives</option>
          </select>

          <button className="btn-light" onClick={loadMessages}>
            Rafraîchir
          </button>

          <button
            className="btn-light"
            onClick={handleSyncCalendrier}
            style={{ backgroundColor: "#4caf50", color: "white" }}
          >
            Mettre à jour le calendrier
          </button>

          <button
            className="btn-light"
            onClick={handleExportICS}
            style={{ backgroundColor: "#1976d2", color: "white" }}
          >
            Export to websites
          </button>

          <button
            className="btn-danger"
            onClick={() => {
              localStorage.removeItem("adminAccess");
              logout();
              setAccessGranted(false);
            }}
          >
            Déconnexion
          </button>
        </div>
      </header>

      {/* MAIN */}
      <main className="admin-main">
        {/* TABLEAU */}
        <div className="table-card">
          <div className="table-scroll">
            <table className="nice-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort("createdAt")}>
                    Date {sortBy === "createdAt" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => toggleSort("nom")}>
                    Nom {sortBy === "nom" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Pers.</th>
                  <th>Période</th>
                  <th onClick={() => toggleSort("status")}>
                    Statut {sortBy === "status" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedMessages.map((m) => {
                  const dateStr =
                    m.createdAt?.toDate?.().toLocaleString?.() || "—";
                  const isAccepted = m.status === "accepted";
                  const isSelected = selectedMessage && selectedMessage.id === m.id;

                  return (
                    <tr
                      key={m.id}
                      className={
                        (isSelected ? "row-active " : "") +
                        (isAccepted ? "row-accepted" : "")
                      }
                      style={
                        isAccepted
                          ? { backgroundColor: "rgba(144,238,144,0.3)" }
                          : {}
                      }
                    >
                      <td>{dateStr}</td>
                      <td>{m.prenom} {m.nom}</td>
                      <td className="cell-email">{m.email}</td>
                      <td>{m.telephone}</td>
                      <td className="cell-center">{m.personnes}</td>
                      <td>{m.periodes}</td>
                      <td>
                        <span className={`status-badge status-${m.status || "new"}`}>
                          {m.status || "new"}
                        </span>
                      </td>
                      <td className="cell-actions">
                        <button
                          className="btn-small"
                          onClick={() => setSelectedMessage(m)}
                        >
                          Lire
                        </button>

                        <button
                          className="btn-small btn-ok"
                          onClick={() => handleSetStatus(m.id, "read")}
                          disabled={isAccepted}
                        >
                          Lu
                        </button>

                        {!isAccepted ? (
                          <button
                            className="btn-small btn-ok"
                            style={{ backgroundColor: "#8bc34a" }}
                            onClick={() => handleAcceptReservation(m)}
                          >
                            Accepter
                          </button>
                        ) : (
                          <button
                            className="btn-small btn-del"
                            style={{ backgroundColor: "#ff9800" }}
                            onClick={() => handleCancelReservation(m)}
                          >
                            Annuler
                          </button>
                        )}

                        <button
                          className="btn-small btn-arch"
                          onClick={() => handleSetStatus(m.id, "archived")}
                          disabled={isAccepted}
                        >
                          Archiver
                        </button>

                        <button
                          className="btn-small btn-del"
                          onClick={() => handleDelete(m.id)}
                        >
                          Suppr.
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {sortedMessages.length === 0 && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "2rem" }}>
                      Aucun message.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PANNEAU DROITE */}
        <div className="detail-card">
          {selectedMessage ? (
            <>
              <div className="detail-header">
                <div className="detail-title">
                  {selectedMessage.prenom} {selectedMessage.nom}
                </div>
                <div className="detail-sub">
                  {selectedMessage.createdAt?.toDate?.().toLocaleString?.() || "—"}
                </div>
              </div>

              <div className="detail-info-grid">
                <div>
                  <div className="label">Email</div>
                  <div className="value">{selectedMessage.email}</div>
                </div>
                <div>
                  <div className="label">Téléphone</div>
                  <div className="value">{selectedMessage.telephone}</div>
                </div>
                <div>
                  <div className="label">Personnes</div>
                  <div className="value">{selectedMessage.personnes}</div>
                </div>
                <div>
                  <div className="label">Période</div>
                  <div className="value">{selectedMessage.periodes}</div>
                </div>
                <div>
                  <div className="label">Statut</div>
                  <div className="value">
                    <span
                      className={`status-badge status-${
                        selectedMessage.status || "new"
                      }`}
                    >
                      {selectedMessage.status || "new"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-message-block">
                <div className="label">Message du client</div>
                <div className="msg-box">{selectedMessage.message}</div>
              </div>

              <div className="detail-actions">
                <button
                  className="btn-small btn-ok"
                  onClick={() => handleSetStatus(selectedMessage.id, "read")}
                  disabled={selectedMessage.status === "accepted"}
                >
                  Marquer lu
                </button>

                {selectedMessage.status !== "accepted" ? (
                  <button
                    className="btn-small btn-ok"
                    style={{ backgroundColor: "#8bc34a" }}
                    onClick={() => handleAcceptReservation(selectedMessage)}
                  >
                    Accepter
                  </button>
                ) : (
                  <button
                    className="btn-small btn-del"
                    style={{ backgroundColor: "#ff9800" }}
                    onClick={() => handleCancelReservation(selectedMessage)}
                  >
                    Annuler
                  </button>
                )}

                <button
                  className="btn-small btn-arch"
                  onClick={() => handleSetStatus(selectedMessage.id, "archived")}
                  disabled={selectedMessage.status === "accepted"}
                >
                  Archiver
                </button>

                <button
                  className="btn-small btn-del"
                  onClick={() => handleDelete(selectedMessage.id)}
                >
                  Supprimer
                </button>

                <button
                  className="btn-small btn-lightgray"
                  onClick={() => setSelectedMessage(null)}
                >
                  Fermer
                </button>

                <a
                  className="btn-small btn-lightgray"
                  href={`mailto:${selectedMessage.email}?subject=Réservation - ${encodeURIComponent(
                    selectedMessage.periodes || ""
                  )}`}
                >
                  Répondre
                </a>
              </div>
            </>
          ) : (
            <div className="empty-detail">
              <div className="empty-detail-title">Sélectionner un message</div>
              <div className="empty-detail-sub">
                Clique sur "Lire" dans le tableau pour voir le détail complet ici.
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
